import React from 'react'
import {
  Award,
  BarChart3,
  BookOpen,
  ClipboardList,
  LayoutDashboard,
  Medal,
  Trophy,
} from 'lucide-react'
import { useLocation, useNavigate } from 'react-router-dom'

import { Button } from '@/components'
import { ROUTES } from '@/constants'
import { cn } from '@/utils/cn'

type ProgressChild = {
  id: string
  name: string
  grade: string
  code: string
  level?: string
}

type LocationState = {
  child?: ProgressChild
}

const DEFAULT_CHILD: ProgressChild = {
  id: '1',
  name: 'Sarah Johnson',
  grade: 'Grade 5th',
  code: 'CAABCD12',
  level: 'Gold Level',
}

const TABS = [
  { id: 'overview', label: 'Overview', icon: LayoutDashboard },
  { id: 'modules', label: 'Modules', icon: BookOpen },
  { id: 'assignments', label: 'Assignment', icon: ClipboardList },
  { id: 'certificates', label: 'Certificates', icon: Award },
  { id: 'leaderboard', label: 'Leaderboard', icon: Trophy },
] as const

type TabId = (typeof TABS)[number]['id']

const QUICK_STATS = [
  {
    label: 'Lessons',
    value: '41 / 60',
    barClass: 'bg-emerald-400',
    bgClass: 'bg-emerald-50',
  },
  {
    label: 'Assignments',
    value: '2 pending',
    barClass: 'bg-sky-400',
    bgClass: 'bg-sky-50',
  },
  {
    label: 'Certificates',
    value: '1 earned',
    barClass: 'bg-amber-400',
    bgClass: 'bg-amber-50',
  },
] as const

const RECENT_ACTIVITY = [
  {
    id: '1',
    title: 'Completed Lesson 14: Loops and Functions',
    time: '2 hours ago',
    tone: 'success' as const,
  },
  {
    id: '2',
    title: 'Submitted Assignment: Number Guessing Game',
    time: '1 day ago',
    tone: 'info' as const,
  },
  {
    id: '3',
    title: 'Earned Gold Level Badge',
    time: '3 days ago',
    tone: 'reward' as const,
  },
  {
    id: '4',
    title: 'Achieved 100% on Quiz 5',
    time: '5 days ago',
    tone: 'success' as const,
  },
] as const

function ProgressDonut({ value }: { value: number }) {
  const size = 120
  const stroke = 10
  const radius = (size - stroke) / 2
  const circumference = 2 * Math.PI * radius
  const offset = circumference - (value / 100) * circumference

  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#E5E7EB"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#34A853"
          strokeWidth={stroke}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className="transition-all duration-500"
        />
      </svg>
      <div className="absolute flex flex-col items-center justify-center">
        <span className="text-2xl font-semibold text-gray-900">{value}%</span>
        <span className="mt-0.5 text-[11px] text-gray-500">Overall</span>
      </div>
    </div>
  )
}

export const ParentProgress: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const state = (location.state ?? {}) as LocationState
  const child = state.child ?? DEFAULT_CHILD

  const [activeTab, setActiveTab] = React.useState<TabId>('overview')

  const initials = child.name
    .split(' ')
    .filter(Boolean)
    .map(part => part[0])
    .join('')

  return (
    <div className="space-y-6">
      <button
        type="button"
        onClick={() => navigate(ROUTES.PARENT.DASHBOARD)}
        className="inline-flex items-center gap-2 text-xs font-medium text-gray-500 hover:text-gray-800"
      >
        <span className="h-5 w-5 rounded-full border border-gray-300 flex items-center justify-center text-[11px]">
          ←
        </span>
        Back to dashboard
      </button>

      <div className="space-y-4">
        <div>
          <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-gray-400">
            Progress &amp; Reports
          </p>
          <h1 className="mt-1 text-xl font-semibold text-gray-900">
            {child.name}
          </h1>
          <p className="mt-1 text-xs text-gray-500">
            {child.grade} · Group class · Wed &amp; Fri, 4:30 PM
          </p>
        </div>

        <div className="rounded-3xl border border-gray-100 bg-white p-5 shadow-sm sm:p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 text-sm font-semibold text-amber-800 shadow-sm">
                {initials}
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">
                  Sarah Johnson
                </p>
                <p className="mt-0.5 text-[11px] text-gray-500">
                  Grade 5th · Group class · Wed &amp; Fri · 4:30 PM
                </p>
                <span className="mt-1 inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-[11px] font-medium text-amber-800">
                  {child.level ?? 'Gold Level'}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-gray-50 px-3 py-2 text-right">
                <p className="text-[11px] text-gray-500">Current module</p>
                <p className="text-xs font-semibold text-gray-900">
                  Python for Beginners
                </p>
              </div>
              <Button
                type="button"
                variant="ghost"
                className="hidden rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 sm:inline-flex"
                icon={<BarChart3 className="h-3.5 w-3.5" />}
              >
                Download summary
              </Button>
            </div>
          </div>

          <div className="mt-5 inline-flex w-full gap-1 rounded-2xl bg-gray-100 p-1">
            {TABS.map(tab => {
              const Icon = tab.icon
              const isActive = tab.id === activeTab
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    'flex flex-1 items-center justify-center gap-2 rounded-xl px-3 py-2 text-xs font-medium transition-colors',
                    isActive
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-800'
                  )}
                >
                  <Icon className="h-3.5 w-3.5" />
                  <span>{tab.label}</span>
                </button>
              )
            })}
          </div>
        </div>
      </div>

      {activeTab === 'overview' && (
        <>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
              <h3 className="text-sm font-semibold text-gray-900">
                Overall Progress
              </h3>
              <p className="mt-1 text-xs text-gray-500">
                Keep up the great work!
              </p>
              <div className="mt-4 flex items-center justify-center">
                <ProgressDonut value={55} />
              </div>
            </div>

            <div className="rounded-2xl border border-gray-100 bg-white p-5 text-center shadow-sm">
              <h3 className="text-sm font-semibold text-gray-900">
                Current Level
              </h3>
              <div className="mt-4 flex flex-col items-center justify-center gap-3">
                <div className="flex h-20 w-20 items-center justify-center rounded-full bg-amber-50">
                  <Medal className="h-10 w-10 text-amber-500" />
                </div>
                <span className="inline-flex items-center rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800">
                  {child.level ?? 'Gold Level'}
                </span>
              </div>
            </div>

            <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
              <h3 className="text-sm font-semibold text-gray-900">
                Quick Stats
              </h3>
              <div className="mt-4 space-y-3">
                {QUICK_STATS.map(stat => (
                  <div
                    key={stat.label}
                    className={cn(
                      'flex items-center justify-between rounded-xl px-3 py-2 text-xs',
                      stat.bgClass
                    )}
                  >
                    <span className="font-medium text-gray-800">
                      {stat.label}
                    </span>
                    <span className="flex items-center gap-2 text-[11px] text-gray-700">
                      <span className="inline-flex h-1.5 w-10 overflow-hidden rounded-full bg-gray-200">
                        <span
                          className={cn(
                            'h-1.5 w-2/3 rounded-full',
                            stat.barClass
                          )}
                        />
                      </span>
                      {stat.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm sm:p-7">
            <h3 className="text-sm font-semibold text-gray-900">
              Recent Activity
            </h3>
            <div className="mt-4 space-y-3">
              {RECENT_ACTIVITY.map(activity => (
                <div
                  key={activity.id}
                  className="flex items-start gap-3 rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3"
                >
                  <div className="mt-0.5">
                    <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
                      •
                    </span>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">
                      {activity.title}
                    </p>
                    <p className="mt-0.5 text-[11px] text-gray-500">
                      {activity.time}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {activeTab !== 'overview' && (
        <div className="rounded-3xl border border-dashed border-gray-200 bg-gray-50 px-6 py-10 text-center text-sm text-gray-500">
          Detailed <span className="font-medium">{activeTab}</span> view coming
          soon. For now, use the Overview tab to see your child&apos;s progress.
        </div>
      )}
    </div>
  )
}
